import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Server, Eye, Clock, FileText } from 'lucide-react'

export function TermsPage() {
  return (
    <div className="flex-1 py-8 px-4">
      <article className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao início
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Termos e Condições & Política de Privacidade
          </h1>
          <p className="text-gray-600">
            Última atualização: {new Date().toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </header>

        <div className="prose prose-lg max-w-none space-y-8">
          
          {/* Processamento Local */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Processamento Local dos Dados do Formulário
                </h2>
                <p className="text-gray-600 mb-4">
                  Os dados que introduz nos formulários (nome, morada, datas, etc.) são processados 
                  <strong> exclusivamente no seu dispositivo</strong> (browser). Estes dados:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span><strong>Nunca são enviados</strong> para os nossos servidores durante o preenchimento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>O PDF é <strong>gerado localmente</strong> no seu browser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>A assinatura digital é processada <strong>apenas no seu dispositivo</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>Os links pré-preenchidos codificam os dados <strong>no próprio URL</strong>, sem passar pelo servidor</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Guardar na Cloud */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Server className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Opção "Guardar na Cloud"
                </h2>
                <p className="text-gray-600 mb-4">
                  Se optar por usar a funcionalidade <strong>"Guardar na Cloud"</strong>, o documento PDF 
                  gerado será enviado para os nossos servidores. Neste caso:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">⚠</span>
                    <span>O ficheiro PDF é armazenado no <strong>Cloudflare R2</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">⚠</span>
                    <span>O documento fica acessível através de um <strong>link único</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">⚠</span>
                    <span>O ficheiro é <strong>automaticamente eliminado após 30 dias</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">⚠</span>
                    <span>Qualquer pessoa com o link pode aceder ao documento durante esse período</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  Esta funcionalidade é <strong>totalmente opcional</strong>. Se preferir manter total 
                  privacidade, use apenas a opção "Gerar e Descarregar PDF".
                </p>
              </div>
            </div>
          </section>

          {/* Dados Cloudflare */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Dados Recolhidos Automaticamente pela Cloudflare
                </h2>
                <p className="text-gray-600 mb-4">
                  Este site é alojado na <strong>Cloudflare</strong>, que recolhe automaticamente 
                  determinados dados técnicos para fins de segurança, desempenho e análise:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>Endereço IP</strong> - para proteção contra ataques e análise geográfica agregada</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>User Agent</strong> - informação sobre o browser e sistema operativo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>País/Região</strong> - localização aproximada baseada no IP</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>Páginas visitadas</strong> - URLs acedidos no site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>Data e hora</strong> - timestamp dos pedidos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span><strong>Referrer</strong> - site de origem (se aplicável)</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  Estes dados são processados pela Cloudflare de acordo com a sua 
                  <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline ml-1">
                    Política de Privacidade
                  </a>.
                </p>
              </div>
            </div>
          </section>

          {/* Cloudflare Turnstile */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Cloudflare Turnstile (Verificação de Segurança)
                </h2>
                <p className="text-gray-600 mb-4">
                  Utilizamos o <strong>Cloudflare Turnstile</strong> para proteger o site contra bots e 
                  abusos. Este serviço pode recolher:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span>Dados de interação com a página (movimentos do rato, cliques)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span>Informações do dispositivo e browser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span>Tokens de verificação temporários</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  O Turnstile foi concebido para ser menos invasivo que CAPTCHAs tradicionais e não 
                  utiliza cookies de tracking.
                </p>
              </div>
            </div>
          </section>

          {/* Estatísticas */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Estatísticas Anónimas
                </h2>
                <p className="text-gray-600 mb-4">
                  Recolhemos estatísticas <strong>completamente anónimas</strong> sobre a utilização do serviço:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>Número de PDFs gerados (sem conteúdo)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>Número de links pré-preenchidos criados</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>Número de uploads para a cloud</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  Estas estatísticas <strong>não contêm dados pessoais identificáveis</strong>. Apenas 
                  contamos ações agregadas por dia, sem qualquer ligação a utilizadores individuais.
                </p>
              </div>
            </div>
          </section>

          {/* Retenção */}
          <section className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
                  Retenção de Dados
                </h2>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span><strong>Dados do formulário:</strong> Não são retidos (processamento local)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span><strong>Ficheiros na cloud:</strong> Eliminados automaticamente após 30 dias</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span><strong>Logs da Cloudflare:</strong> Retidos conforme política da Cloudflare (tipicamente 72h-30 dias)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span><strong>Estatísticas:</strong> Agregadas e anónimas, retidas indefinidamente</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contacto */}
          <section className="card p-6 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 mt-0 mb-2">
              Contacto
            </h2>
            <p className="text-gray-600">
              Para questões sobre privacidade ou para exercer os seus direitos ao abrigo do RGPD, 
              contacte-nos através de:
            </p>
            <p className="mt-2">
              <a href="mailto:cessarcontrato@pedrorualves.eu" className="text-primary-600 hover:underline font-medium">
                cessarcontrato@pedrorualves.eu
              </a>
            </p>
          </section>

        </div>
      </article>
    </div>
  )
}
